import React, { useEffect, useState } from 'react';
import Container from '../Container/Container';
import { useNavigate } from 'react-router';
import { get, post } from '../ApiServices/ApiServices';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useRef } from 'react';
import DeleteButton from './Component/DeleteFun';
import Loader from './Component/Loader';

// Validation schema
const validationSchema = Yup.object().shape({
    coatId: Yup.string().required('Coat style is required'),
    tier: Yup.string().required('Tier is required'),
    coatOnlyRetail: Yup.number()
        .required('Retail price is required')
        .positive('Price must be positive'),
    coatOnlyWholesale: Yup.number()
        .required('Wholesale price is required')
        .positive('Price must be positive'),
    completeRetail: Yup.number()
        .required('Complete retail price is required')
        .positive('Price must be positive'),
    completeWholesale: Yup.number()
        .required('Complete wholesale price is required')
        .positive('Price must be positive')
});

const individualItemValidationSchema = Yup.object().shape({
    itemId: Yup.string().required('Item is required'),
    rentalPrice: Yup.number()
        .required('Rental price is required')
        .positive('Price must be positive'),
    replacementCost: Yup.number()
        .required('Replacement cost is required')
        .positive('Cost must be positive')
});

function Pricing() {
    const [tierPrices, setTierPrices] = useState([]);
    const [coatData, setCoatData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmittingIndividual, setIsSubmittingIndividual] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [individualItem, setindividualItem] = useState([]);
    const [individualItemsPerPage, setIndividualItemsPerPage] = useState(10);
    const [currentIndividualItemPage, setCurrentIndividualItemPage] = useState(1);
    const [totalIndividualItems, setTotalIndividualItems] = useState(0);
    const [inventory, setinventory] = useState([]);
    const [type, settype] = useState(1);
    const [loadingTierPrices, setLoadingTierPrices] = useState(false);
    const [loadingIndividualItems, setLoadingIndividualItems] = useState(false);
    const [loadingCoatData, setLoadingCoatData] = useState(false);
    const [loadingInventory, setLoadingInventory] = useState(false);
    const [filterTier, setFilterTier] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const ref1 = useRef();
    const ref2 = useRef();

    const getTierPrice = async (page = currentPage, limit = itemsPerPage, tier = filterTier, search = searchTerm) => {
        try {
            setLoadingTierPrices(true);
            let api = `coatstyle-tier-price?page_number=${page}&page_size=${limit}`;

            if (tier) {
                api += `&tier=${tier}`;
            }

            if (search) {
                api += `&search=${search}`;
            }

            const response = await get(api);
            if (response?.status === true) {
                setTierPrices(response?.data?.rows);
                setTotalItems(response?.data?.count || 0);
            }
        } catch (err) {
            if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
            console.error("Error fetching tier prices:", err);
            // toast.error("Failed to fetch tier prices");
        } finally {
            setLoadingTierPrices(false);
        }
    };

    const getindividualPrice = async (page = currentIndividualItemPage, limit = individualItemsPerPage) => {
        try {
            setLoadingIndividualItems(true);
            const api = `individual-item-price?page_number=${page}&page_size=${limit}`;
            const response = await get(api);
            if (response?.status === true) {
                setindividualItem(response?.data?.rows);
                setTotalIndividualItems(response?.data?.count || 0);
            }
        } catch (err) {
            if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
            console.error("Error fetching individual item prices:", err);
            toast.error("Failed to fetch individual item prices");
        } finally {
            setLoadingIndividualItems(false);
        }
    };

    const getinventories = async () => {
        try {
            setLoadingInventory(true);
            const api = `all-seperate-rental-replacement-costs`;
            const response = await get(api);
            if (response?.status == true) {
                setinventory(response?.data);
            }
        } catch (err) {
            if (err?.message == "Invalid or expired token" || err?.message == "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
            console.error("Error fetching inventory data:", err);
            toast.error("Failed to fetch inventory data");
        } finally {
            setLoadingInventory(false);
        }
    };

    useEffect(() => {
        getinventories();
        getindividualPrice();
    }, []);

    const fetchCoatInventoryData = async () => {
        try {
            setLoadingCoatData(true);
            const api = `all-inventories?category_id=${1}&size=${1000}&active=${true}&orderby=asc&sortby=style`;
            const totalData = await get(api);
            setCoatData(totalData?.data?.rows);
        } catch (err) {
            console.error('Error fetching inventory data:', err);
            toast.error("Failed to fetch coat inventory data");
        } finally {
            setLoadingCoatData(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        getTierPrice(newPage, itemsPerPage, filterTier, searchTerm);
    };

    const handleIndividualItemPageChange = (newPage) => {
        setCurrentIndividualItemPage(newPage);
        getindividualPrice(newPage, individualItemsPerPage);
    };

    const handleTierFilterChange = (e) => {
        const tier = e.target.value;
        setFilterTier(tier);
        setCurrentPage(1);
        getTierPrice(1, itemsPerPage, tier, searchTerm);
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCurrentPage(1);
        getTierPrice(1, itemsPerPage, filterTier, term);
    };

    const formik = useFormik({
        initialValues: {
            coatId: '',
            tier: '',
            coatOnlyRetail: '',
            coatOnlyWholesale: '',
            completeRetail: '',
            completeWholesale: ''
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsSubmitting(true);
            try {
                const api = `coatstyle-tier-price/${values.coatId}`;

                const addDollarSign = (value) => {
                    if (!value) return value;
                    const stringValue = String(value);
                    return stringValue.startsWith('$') ? stringValue : `$${stringValue}`;
                };

                const payload = {
                    tier: values.tier,
                    coat_only_retail: addDollarSign(values.coatOnlyRetail),
                    coat_only_wholesale: addDollarSign(values.coatOnlyWholesale),
                    complete_retail: addDollarSign(values.completeRetail),
                    complete_wholesale: addDollarSign(values.completeWholesale),
                };
                const response = await post(api, payload);

                if (response?.status === true) {
                    ref1.current.click();
                    toast.success(response?.message, {
                        position: "top-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        closeButton: false,
                    });
                    getTierPrice(currentPage, itemsPerPage, filterTier, searchTerm);
                    resetForm();
                } else {
                    alert(response?.message || 'Failed to add tier price');
                }
            } catch (err) {
                if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                    localStorage.removeItem('token');
                    navigate('/login');
                } else {
                    console.error("Error adding tier price:", err);
                    alert('An error occurred while adding the tier price');
                }
            } finally {
                setIsSubmitting(false);
            }
        }
    });

    const individualItemFormik = useFormik({
        initialValues: {
            id: '',
            itemId: '',
            category_id: '',
            name: '',
            type: '',
            rentalPrice: '',
            replacementCost: '',
            isEdit: false,
            editId: null
        },
        validationSchema: individualItemValidationSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsSubmittingIndividual(true);
            try {
                const api = values.isEdit
                    ? `edit-individual-item-price/${values.editId}`
                    : `add-individual-item-price/${values.id}`;

                const addDollarSign = (value) => {
                    if (!value) return value;
                    const stringValue = String(value);
                    return stringValue.startsWith('$') ? stringValue : `$${stringValue}`;
                };

                // Find the selected item from inventory to get category_id, name, and type
                const selectedItem = inventory.find(item => item.id == values.itemId);

                const payload = {
                    // category_id: selectedItem?.category_id || values.category_id,
                    name: selectedItem?.name || values.name,
                    // type: selectedItem?.type || values.type,
                    rental_price: addDollarSign(values.rentalPrice),
                    replacement_cost: addDollarSign(values.replacementCost)
                };

                const response = values.isEdit
                    ? await post(api, payload)
                    : await post(api, payload);

                if (response?.status === true) {
                    ref2.current.click();
                    toast.success(response?.message, {
                        position: "top-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        closeButton: false,
                    });
                    getindividualPrice(currentIndividualItemPage, individualItemsPerPage);
                    resetForm();
                } else {
                    alert(response?.message || (values.isEdit ? 'Failed to update item price' : 'Failed to add item price'));
                }
            } catch (err) {
                if (err?.message == "Invalid or expired token" || err?.message == "Invalid Token") {
                    localStorage.removeItem('token');
                    navigate('/login');
                } else {
                    console.error("Error in operation:", err);
                    alert(err?.message);
                }
            } finally {
                setIsSubmittingIndividual(false);
            }
        }
    });

    const Singleiteam = async (id) => {
        try {
            const api = `get-individual-item-price/${id}`;
            const response = await get(api);
            if (response?.status == true) {
                individualItemFormik.setValues({
                    itemId: response?.data?.item_id || '',
                    category_id: response?.data?.category_id,
                    name: response?.data?.name,
                    type: response?.data?.type,
                    rentalPrice: response?.data?.rental_price ?
                        String(response?.data?.rental_price).replace(/\$/g, '') : '',
                    replacementCost: response?.data?.replacement_cost ?
                        String(response?.data?.replacement_cost).replace(/\$/g, '') : '',
                    isEdit: true,
                    editId: id
                });
            }
        } catch (err) {
            if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
            console.error("Error fetching inventory data:", err);
        }
    }

    // Add a function to handle the add new button click
    const handleAddNewClick = () => {
        individualItemFormik.resetForm();
        individualItemFormik.setValues({
            itemId: '',
            category_id: '',
            name: '',
            type: '',
            rentalPrice: '',
            replacementCost: '',
            isEdit: false,
            editId: null
        });
    };

    useEffect(() => {
        getTierPrice();
        fetchCoatInventoryData();
    }, []);

    const handleDeleteSuccess = () => {
        getTierPrice(currentPage, itemsPerPage, filterTier, searchTerm);
    };

    const handleIndividualItemDeleteSuccess = () => {
        getindividualPrice(currentIndividualItemPage, individualItemsPerPage);
    };


    const SingleData = async (id) => {
        try {
            const api = `view-inventory/${id}`;
            const response = await get(api);
            if (response?.status === true) {
                formik.setValues({
                    coatId: response?.data?.id || response?.data?.id,
                    tier: response?.data?.tier,
                    coatOnlyRetail: response?.data?.coat_only_retail ?
                        String(response?.data?.coat_only_retail).replace(/\$/g, '') : '',
                    coatOnlyWholesale: response?.data?.coat_only_wholesale ?
                        String(response?.data?.coat_only_wholesale).replace(/\$/g, '') : '',
                    completeRetail: response?.data?.complete_retail ?
                        String(response?.data?.complete_retail).replace(/\$/g, '') : '',
                    completeWholesale: response?.data?.complete_wholesale ?
                        String(response?.data?.complete_wholesale).replace(/\$/g, '') : ''
                });
            }
        } catch (err) {
            if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
            console.error("Error fetching inventory data:", err);
        }
    }

    return (
        <>
            <Container>
                <div className="nk-content">
                    <div className="container-fluid">
                        <div className="nk-content-inner">
                            <div className="nk-content-body">
                                <div className="nk-block-head nk-block-head-sm">
                                    <div className="nk-block-between">
                                        <div className="nk-block-head-content">
                                            <h3 className="nk-block-title page-title">Price List</h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="card card-preview priceList">
                                    <div className="card-inner">
                                        <div className="row align-items-center justify-content-between">
                                            <div className="col-md-6">
                                                <h5>Coat Style Tier Management</h5>
                                                <p>Manage coat styles across four pricing tiers ($70 - $85)</p>
                                            </div>
                                            <div className="col-md-6">
                                                {
                                                    type == 1 && <button type="submit" data-bs-toggle="modal" data-bs-target="#addPriceStyle" className="btn btn-primary f-right">+ Add New Style</button>
                                                }
                                                {
                                                    type == 2 && <button
                                                        type="button"
                                                        className="btn btn-primary f-right" data-bs-toggle="modal"
                                                        data-bs-target="#addIteamPrice"
                                                        onClick={handleAddNewClick}
                                                    >
                                                        Add Individual Item Price
                                                    </button>
                                                }
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-12">
                                                <ul className="nav nav-tabs">
                                                    <li className="nav-item" onClick={() => settype(1)}>
                                                        <a className={`nav-link ${type === 1 ? 'active' : ''}`} data-bs-toggle="tab" href="#tabItem1">Coat Style & Tier Price</a>
                                                    </li>
                                                    <li className="nav-item" onClick={() => settype(2)}>
                                                        <a className={`nav-link ${type === 2 ? 'active' : ''}`} data-bs-toggle="tab" href="#tabItem2">Individual Item Price</a>
                                                    </li>
                                                </ul>

                                                <div className="tab-content">
                                                    <div className={`tab-pane ${type === 1 ? 'active' : ''}`} id="tabItem1">
                                                        <h4>Coat Style & Tier Pricing Management</h4>
                                                        <div className="row">
                                                            <div className="col-md-2">
                                                                <label>Sort by:</label>
                                                                <div className="form-control-select">
                                                                    <select
                                                                        className='form-control'
                                                                        value={filterTier}
                                                                        onChange={handleTierFilterChange}
                                                                    >
                                                                        <option value="">All Tiers</option>
                                                                        <option value="1">Tier 1</option>
                                                                        <option value="2">Tier 2</option>
                                                                        <option value="3">Tier 3</option>
                                                                        <option value="4">Tier 4</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label>Search:</label>
                                                                <div className="form-control-wrap">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Search by color"
                                                                        value={searchTerm}
                                                                        onChange={handleSearchChange}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row mt-3">
                                                            <div className="col-md-12">
                                                                <div className="table-responsive">
                                                                    <table className="table table-striped table-bordered">
                                                                        <thead>
                                                                            <tr>
                                                                                <th scope="col"></th>
                                                                                <th scope="col"></th>
                                                                                <th scope="col"></th>
                                                                                <th scope="col" className="text-center" colSpan="2">Coat Only</th>
                                                                                <th scope="col" className="text-center" colSpan="2">Complete</th>
                                                                                <th scope="col"></th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            <tr>
                                                                                <th scope="row">Style Code</th>
                                                                                <td>Style Name</td>
                                                                                <td>Tier</td>
                                                                                <td>Retail</td>
                                                                                <td>Wholesale</td>
                                                                                <td>Retail</td>
                                                                                <td>Wholesale</td>
                                                                                <td>Actions</td>
                                                                            </tr>
                                                                            {loadingTierPrices ? (
                                                                                <tr>
                                                                                    <td colSpan="8" className="text-center">
                                                                                        <Loader />
                                                                                    </td>
                                                                                </tr>
                                                                            ) : tierPrices.length === 0 ? (
                                                                                <tr>
                                                                                    <td colSpan="8" className="text-center">
                                                                                        No data found
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                tierPrices.map((data) => (
                                                                                    <tr key={data.id}>
                                                                                        <th scope="row">{data?.style}</th>
                                                                                        <td>{data?.description}</td>
                                                                                        <td>{data?.tier}</td>
                                                                                        <td>{data?.coat_only_retail}</td>
                                                                                        <td>{data?.coat_only_wholesale}</td>
                                                                                        <td>{data?.complete_retail}</td>
                                                                                        <td>{data?.complete_wholesale}</td>
                                                                                        <td>
                                                                                            <div className="tableActionBtn-1" style={{ cursor: "pointer" }} >
                                                                                                <button className="editPen" onClick={() => SingleData(data.id)} data-bs-toggle="modal" data-bs-target="#addPriceStyle">
                                                                                                    <em className="icon ni ni-edit-alt"></em>
                                                                                                </button>
                                                                                                <DeleteButton
                                                                                                    url={`delete-coatstyle-tier-price/${data?.id}`}
                                                                                                    onDeleteSuccess={() => handleDeleteSuccess(data.id)}
                                                                                                />
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                    {tierPrices.length > 0 && (
                                                                        <div className="nk-block-between-md g-3 mt-3">
                                                                            <div className="g">
                                                                                <div className="pagination d-flex gap-2" style={{ height: "35px" }}>
                                                                                    <button
                                                                                        className={`btn btn-sm ${currentPage === 1 ? 'btn-disabled' : 'btn-primary'}`}
                                                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                                                        disabled={currentPage === 1}
                                                                                    >
                                                                                        Previous
                                                                                    </button>

                                                                                    {Array.from({ length: Math.ceil(totalItems / itemsPerPage) }, (_, i) => i + 1)
                                                                                        .filter(page =>
                                                                                            page === 1 ||
                                                                                            page === currentPage ||
                                                                                            page === currentPage - 1 ||
                                                                                            page === currentPage + 1 ||
                                                                                            page === Math.ceil(totalItems / itemsPerPage)
                                                                                        )
                                                                                        .map((page, i, array) => (
                                                                                            <React.Fragment key={page}>
                                                                                                {i > 0 && array[i] - array[i - 1] > 1 && (
                                                                                                    <span className="btn btn-sm btn-disabled">...</span>
                                                                                                )}
                                                                                                <button
                                                                                                    className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                                                    onClick={() => handlePageChange(page)}
                                                                                                >
                                                                                                    {page}
                                                                                                </button>
                                                                                            </React.Fragment>
                                                                                        ))}

                                                                                    <button
                                                                                        className={`btn btn-sm ${currentPage === Math.ceil(totalItems / itemsPerPage) ? 'btn-disabled' : 'btn-primary'}`}
                                                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                                                        disabled={currentPage === Math.ceil(totalItems / itemsPerPage)}
                                                                                    >
                                                                                        Next
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className={`tab-pane ${type === 2 ? 'active' : ''}`} id="tabItem2">
                                                        <h4>Individual Item Price Listing</h4>
                                                        <div className="row mt-3">
                                                            <div className="col-md-12">
                                                                <div className="table-responsive">
                                                                    <table className="table table-striped">
                                                                        <thead>
                                                                            <tr>
                                                                                <th scope="col">Item Name</th>
                                                                                <th scope="col">Rental Price</th>
                                                                                <th scope="col">Replacement Cost</th>
                                                                                <th scope="col">Actions</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {loadingIndividualItems ? (
                                                                                <tr>
                                                                                    <td colSpan="4" className="text-center">
                                                                                        <Loader />
                                                                                    </td>
                                                                                </tr>
                                                                            ) : individualItem.length === 0 ? (
                                                                                <tr>
                                                                                    <td colSpan="4" className="text-center">
                                                                                        No data found
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                individualItem.map((data) => (
                                                                                    <tr key={data.id}>
                                                                                        <td>{data?.name}</td>
                                                                                        <td>{data?.rental_price}</td>
                                                                                        <td>{data?.replacement_cost}</td>
                                                                                        <td>
                                                                                            <div className="tableActionBtn-1" style={{ cursor: "pointer" }}>
                                                                                                <button className="editPen" onClick={() => Singleiteam(data?.id)} data-bs-toggle="modal" data-bs-target="#addIteamPrice" >
                                                                                                    <em className="icon ni ni-edit-alt"></em>
                                                                                                </button>
                                                                                                <DeleteButton
                                                                                                    url={`delete-individual-item-price/${data?.id}`}
                                                                                                    onDeleteSuccess={() => handleIndividualItemDeleteSuccess(data?.id)}
                                                                                                />
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                    {individualItem.length > 0 && (
                                                                        <div className="nk-block-between-md g-3 mt-3">
                                                                            <div className="g">
                                                                                <div className="pagination d-flex gap-2" style={{ height: "35px" }}>
                                                                                    <button
                                                                                        className={`btn btn-sm ${currentIndividualItemPage === 1 ? 'btn-disabled' : 'btn-primary'}`}
                                                                                        onClick={() => handleIndividualItemPageChange(currentIndividualItemPage - 1)}
                                                                                        disabled={currentIndividualItemPage === 1}
                                                                                    >
                                                                                        Previous
                                                                                    </button>

                                                                                    {Array.from({ length: Math.ceil(totalIndividualItems / individualItemsPerPage) }, (_, i) => i + 1)
                                                                                        .filter(page =>
                                                                                            page === 1 ||
                                                                                            page === currentIndividualItemPage ||
                                                                                            page === currentIndividualItemPage - 1 ||
                                                                                            page === currentIndividualItemPage + 1 ||
                                                                                            page === Math.ceil(totalIndividualItems / individualItemsPerPage)
                                                                                        )
                                                                                        .map((page, i, array) => (
                                                                                            <React.Fragment key={page}>
                                                                                                {i > 0 && array[i] - array[i - 1] > 1 && (
                                                                                                    <span className="btn btn-sm btn-disabled">...</span>
                                                                                                )}
                                                                                                <button
                                                                                                    className={`btn btn-sm ${currentIndividualItemPage === page ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                                                    onClick={() => handleIndividualItemPageChange(page)}
                                                                                                >
                                                                                                    {page}
                                                                                                </button>
                                                                                            </React.Fragment>
                                                                                        ))}

                                                                                    <button
                                                                                        className={`btn btn-sm ${currentIndividualItemPage === Math.ceil(totalIndividualItems / individualItemsPerPage) ? 'btn-disabled' : 'btn-primary'}`}
                                                                                        onClick={() => handleIndividualItemPageChange(currentIndividualItemPage + 1)}
                                                                                        disabled={currentIndividualItemPage === Math.ceil(totalIndividualItems / individualItemsPerPage)}
                                                                                    >
                                                                                        Next
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Add Price Style Modal */}
                                <div className="modal pr-0Important fade" tabIndex="-1" id="addPriceStyle" data-bs-backdrop="static" data-bs-keyboard="false">
                                    <div className="modal-dialog addPriceListMdal" role="document">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Add Coat Style</h5>
                                                <a className="close" data-bs-dismiss="modal" aria-label="Close" ref={ref1} style={{ cursor: "pointer" }} onClick={formik.resetForm}>
                                                    <em className="icon ni ni-cross-c"></em>
                                                </a>
                                            </div>
                                            <div className="modal-body inputCardBox">
                                                {loadingCoatData ? (
                                                    <div className="text-center">
                                                        <Loader />
                                                    </div>
                                                ) : (
                                                    <form onSubmit={formik.handleSubmit}>
                                                        <div className="row">
                                                            <div className="col-md-12 mb-3">
                                                                <label>Coat (style + code) *</label>
                                                                <div className="form-control-select">
                                                                    <select
                                                                        className={`form-control ${formik.touched.coatId && formik.errors.coatId ? 'is-invalid' : ''}`}
                                                                        name="coatId"
                                                                        value={formik.values.coatId}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                    >
                                                                        <option value="">Select Coat Style</option>
                                                                        {coatData.map((data) => (
                                                                            <option key={data.id} value={data.id}>
                                                                                {data.style} {data.description}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {formik.touched.coatId && formik.errors.coatId && (
                                                                        <div className="invalid-feedback">{formik.errors.coatId}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="col-md-12 mb-3">
                                                                <label>Tier *</label>
                                                                <div className="form-control-select">
                                                                    <select
                                                                        className={`form-control ${formik.touched.tier && formik.errors.tier ? 'is-invalid' : ''}`}
                                                                        name="tier"
                                                                        value={formik.values.tier}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                    >
                                                                        <option value="">Select Tier</option>
                                                                        <option value="1">Tier 1</option>
                                                                        <option value="2">Tier 2</option>
                                                                        <option value="3">Tier 3</option>
                                                                        <option value="4">Tier 4</option>
                                                                    </select>
                                                                    {formik.touched.tier && formik.errors.tier && (
                                                                        <div className="invalid-feedback">{formik.errors.tier}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Coat Only (Retail) *</label>
                                                                <div className="input-group">
                                                                    <span className="input-group-text">$</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${formik.touched.coatOnlyRetail && formik.errors.coatOnlyRetail ? 'is-invalid' : ''}`}
                                                                        name="coatOnlyRetail"
                                                                        value={formik.values.coatOnlyRetail}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                </div>
                                                                {formik.touched.coatOnlyRetail && formik.errors.coatOnlyRetail && (
                                                                    <div className="invalid-feedback">{formik.errors.coatOnlyRetail}</div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Coat Only (Wholesale) *</label>
                                                                <div className="input-group">
                                                                    <span className="input-group-text">$</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${formik.touched.coatOnlyWholesale && formik.errors.coatOnlyWholesale ? 'is-invalid' : ''}`}
                                                                        name="coatOnlyWholesale"
                                                                        value={formik.values.coatOnlyWholesale}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                </div>
                                                                {formik.touched.coatOnlyWholesale && formik.errors.coatOnlyWholesale && (
                                                                    <div className="invalid-feedback">{formik.errors.coatOnlyWholesale}</div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Complete (Retail) *</label>
                                                                <div className="input-group">
                                                                    <span className="input-group-text">$</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${formik.touched.completeRetail && formik.errors.completeRetail ? 'is-invalid' : ''}`}
                                                                        name="completeRetail"
                                                                        value={formik.values.completeRetail}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                </div>
                                                                {formik.touched.completeRetail && formik.errors.completeRetail && (
                                                                    <div className="invalid-feedback">{formik.errors.completeRetail}</div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Complete (Wholesale) *</label>
                                                                <div className="input-group">
                                                                    <span className="input-group-text">$</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${formik.touched.completeWholesale && formik.errors.completeWholesale ? 'is-invalid' : ''}`}
                                                                        name="completeWholesale"
                                                                        value={formik.values.completeWholesale}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                </div>
                                                                {formik.touched.completeWholesale && formik.errors.completeWholesale && (
                                                                    <div className="invalid-feedback">{formik.errors.completeWholesale}</div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-12">
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary w-100 justify-content-center"
                                                                    disabled={isSubmitting}
                                                                >
                                                                    {isSubmitting ? 'Adding...' : 'Add Style'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                {/* Add Item Price Modal */}
                                <div className="modal pr-0Important fade" tabIndex="-1" id="addIteamPrice" data-bs-backdrop="static" data-bs-keyboard="false">
                                    <div className="modal-dialog addPriceListMdal" role="document">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Add Individual Item Price</h5>
                                                <a className="close" data-bs-dismiss="modal" aria-label="Close" ref={ref2} style={{ cursor: "pointer" }} onClick={individualItemFormik.resetForm}>
                                                    <em className="icon ni ni-cross-c"></em>
                                                </a>
                                            </div>
                                            <div className="modal-body inputCardBox">
                                                {loadingInventory ? (
                                                    <div className="text-center">
                                                        <Loader />
                                                    </div>
                                                ) : (
                                                    <form onSubmit={individualItemFormik.handleSubmit}>
                                                        <div className="row">
                                                            <div className="col-md-12 mb-3">
                                                                <label>Item *</label>
                                                                <div className="form-control-select">
                                                                    <select
                                                                        className={`form-control ${individualItemFormik.touched.itemId && individualItemFormik.errors.itemId ? 'is-invalid' : ''}`}
                                                                        name="itemId"
                                                                        value={individualItemFormik.values.itemId}
                                                                        onChange={(e) => {
                                                                            // Get the selected item ID
                                                                            const selectedId = e.target.value;
                                                                            // Find the complete item data from inventory
                                                                            const selectedItem = inventory.find(item => item.id == selectedId);

                                                                            // Update Formik values
                                                                            individualItemFormik.setValues({
                                                                                ...individualItemFormik.values,
                                                                                itemId: selectedId,
                                                                                category_id: selectedItem?.category_id || '',
                                                                                name: selectedItem?.name || '',
                                                                                type: selectedItem?.type || '',
                                                                                id: selectedItem?.id || ''

                                                                            });
                                                                        }}
                                                                        onBlur={individualItemFormik.handleBlur}
                                                                    >
                                                                        <option value="">Select Item</option>
                                                                        {inventory.map((data) => (
                                                                            <option key={data.id} value={data.id}>
                                                                                {data.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {individualItemFormik.touched.itemId && individualItemFormik.errors.itemId && (
                                                                        <div className="invalid-feedback">{individualItemFormik.errors.itemId}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6 mb-3">
                                                                <label>Rental Price *</label>
                                                                <div className="input-group">
                                                                    <span className="input-group-text">$</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${individualItemFormik.touched.rentalPrice && individualItemFormik.errors.rentalPrice ? 'is-invalid' : ''}`}
                                                                        name="rentalPrice"
                                                                        value={individualItemFormik.values.rentalPrice}
                                                                        onChange={individualItemFormik.handleChange}
                                                                        onBlur={individualItemFormik.handleBlur}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                </div>
                                                                {individualItemFormik.touched.rentalPrice && individualItemFormik.errors.rentalPrice && (
                                                                    <div className="invalid-feedback">{individualItemFormik.errors.rentalPrice}</div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Replacement Cost *</label>
                                                                <div className="input-group">
                                                                    <span className="input-group-text">$</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${individualItemFormik.touched.replacementCost && individualItemFormik.errors.replacementCost ? 'is-invalid' : ''}`}
                                                                        name="replacementCost"
                                                                        value={individualItemFormik.values.replacementCost}
                                                                        onChange={individualItemFormik.handleChange}
                                                                        onBlur={individualItemFormik.handleBlur}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                </div>
                                                                {individualItemFormik.touched.replacementCost && individualItemFormik.errors.replacementCost && (
                                                                    <div className="invalid-feedback">{individualItemFormik.errors.replacementCost}</div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-12">
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary w-100 justify-content-center"
                                                                    disabled={isSubmittingIndividual}
                                                                >
                                                                    {isSubmittingIndividual
                                                                        ? (individualItemFormik.values.isEdit ? 'Updating...' : 'Adding...')
                                                                        : (individualItemFormik.values.isEdit ? 'Update Item Price' : 'Add Item Price')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container >
        </>
    )
}

export default Pricing;